import { enumerateColumns } from './columns.js'
import type { BonusRule, Column, PricedSelection, PricedSlip } from './types.js'

/** Margine per l'arrotondamento per difetto: 25 × 1.14 in virgola mobile fa 28,4999…, la vincita giusta è 28. */
const EPSILON = 1e-6

/** Bonus della colonna: la regola più alta raggiunta dal numero di eventi. */
export function bonusPct(bonus: readonly BonusRule[], legs: number): number {
  return bonus.reduce((best, rule) => (legs >= rule.legs && rule.pct > best ? rule.pct : best), 0)
}

const effectiveOdds = (pick: PricedSelection) => (pick.status === 'void' ? 1 : pick.odds / (pick.deadHeat ?? 1))

/**
 * Vincita di una colonna con tutti gli esiti vinti o rimborsati (spec 4): floor(puntata × Π quote × (1 + bonus/100)).
 * Un esito rimborsato vale 1 e non conta per il bonus.
 */
export function columnPayout(picks: readonly PricedSelection[], stake: number, bonus: readonly BonusRule[]): number {
  const counted = picks.filter((p) => p.status !== 'void')
  const odds = picks.reduce((product, p) => product * effectiveOdds(p), 1)
  return Math.floor(stake * odds * (1 + bonusPct(bonus, counted.length) / 100) + EPSILON)
}

export type SlipSettlement = {
  status: 'open' | 'won' | 'lost'
  /** null finché aperta e quando persa. */
  payout: number | null
  columns: { total: number; won: number; lost: number; open: number }
}

const picksOf = (slip: PricedSlip, column: Column) => column.map(({ leg, selection }) => slip.legs[leg].selections[selection])

/**
 * Dai soli stati degli esiti (spec 4): una colonna è persa con un esito perso, aperta con un esito aperto, altrimenti
 * vinta. La schedina si chiude quando nessuna colonna è aperta; vincita = somma delle vinte, al massimo `maxPayout`.
 */
export function settleSlip(slip: PricedSlip): SlipSettlement {
  const columns = enumerateColumns(slip)
  const counts = { total: columns.length, won: 0, lost: 0, open: 0 }
  let payout = 0
  for (const column of columns) {
    const picks = picksOf(slip, column)
    if (picks.some((p) => p.status === 'lost')) counts.lost++
    else if (picks.some((p) => p.status === 'open')) counts.open++
    else {
      counts.won++
      payout += columnPayout(picks, slip.stakePerColumn, slip.bonus)
    }
  }
  if (counts.open > 0) return { status: 'open', payout: null, columns: counts }
  const capped = slip.maxPayout === null ? payout : Math.min(payout, slip.maxPayout)
  return capped > 0 ? { status: 'won', payout: capped, columns: counts } : { status: 'lost', payout: null, columns: counts }
}

/**
 * Vincita possibile con gli esiti non ancora persi. Minima: la colonna meno ricca. Massima: un solo esito vincente
 * per evento (le alternative sulla stessa partita si escludono), il più quotato.
 */
export function slipPotential(slip: PricedSlip): { min: number; max: number } {
  const alive: PricedSlip = { ...slip, legs: slip.legs.map((leg) => ({ ...leg, selections: leg.selections.filter((s) => s.status !== 'lost') })) }
  const columns = enumerateColumns(alive)
  if (columns.length === 0) return { min: 0, max: 0 }
  const cap = (n: number) => (slip.maxPayout === null ? n : Math.min(n, slip.maxPayout))
  const min = columns.reduce((minVal, column) => Math.min(minVal, columnPayout(picksOf(alive, column), slip.stakePerColumn, slip.bonus)), Infinity)
  const best: PricedSlip = {
    ...alive,
    legs: alive.legs.map((leg) => ({ ...leg, selections: leg.selections.length ? [leg.selections.reduce((a, b) => (effectiveOdds(b) > effectiveOdds(a) ? b : a))] : [] })),
  }
  const max = enumerateColumns(best).reduce((sum, column) => sum + columnPayout(picksOf(best, column), slip.stakePerColumn, slip.bonus), 0)
  return { min: cap(min), max: cap(max) }
}
